/**
 * How a tenant's access to a feature is resolved — the ONE definition.
 *
 * Precedence is `assignment ?? default`: an explicit per-tenant assignment
 * wins outright, and the feature's `defaultActivated` flag applies only when
 * no assignment exists.
 *
 * Why this file exists rather than the rule living in each resolver: the rule
 * had two implementations — `src/useFeature.ts` (enforcement) and
 * `wfe/src/features/things/useFeatures.ts` (what the UI shows) — and both
 * spelled it as an OR. A UI that disagrees with enforcement is worse than
 * either being wrong, because the operator cannot tell which one lied. Both
 * now call in here, so the two cannot drift apart (LOCKER-408 DoD1 + DoD2).
 *
 * Before LOCKER-408 the rule was `assignment || default`, which made
 * "off for this one organization" unreachable while the default was on. Prod
 * carries `defaultActivated: false` on the only feature that exists, so the
 * change revokes nothing on deploy (LOCKER-408 N1).
 */

/** The minimum an assignment must expose to be resolved. */
export type FeatureActivationSource = {
  isActivated?: boolean | null;
};

/**
 * What a tenant's row is, once resolved. Four states, because "off" and
 * "nobody has decided yet" are different facts about the rollout even though
 * they grant the same access.
 */
export enum FeatureAccessState {
  /** An assignment exists and is activated. */
  ActivatedByAssignment = "ActivatedByAssignment",
  /** An assignment exists and is deactivated. Authoritative — beats the default. */
  DeactivatedByAssignment = "DeactivatedByAssignment",
  /** No assignment; the feature's default grants access. */
  ActivatedByDefault = "ActivatedByDefault",
  /** No assignment and the default is off. */
  Unassigned = "Unassigned",
}

/**
 * Resolve one (assignment, default) pair to its state.
 *
 * `assignment` is the tenant's assignment for this feature, or
 * `undefined` / `null` when none exists. An assignment whose `isActivated` is
 * itself null/undefined is treated as absent: the field is required on write,
 * so a missing value is a malformed record rather than a deliberate "off", and
 * inheriting the default is the non-destructive reading.
 */
export const resolveFeatureAccessState = (
  assignment: FeatureActivationSource | null | undefined,
  defaultActivated: boolean | null | undefined,
): FeatureAccessState => {
  const assigned = assignment?.isActivated;
  if (assigned === true) return FeatureAccessState.ActivatedByAssignment;
  if (assigned === false) return FeatureAccessState.DeactivatedByAssignment;
  return defaultActivated === true
    ? FeatureAccessState.ActivatedByDefault
    : FeatureAccessState.Unassigned;
};

/** The two states that grant access. */
export const isFeatureAccessGranted = (state: FeatureAccessState): boolean =>
  state === FeatureAccessState.ActivatedByAssignment ||
  state === FeatureAccessState.ActivatedByDefault;

/**
 * `assignment ?? default`, collapsed to the boolean an enforcement check
 * needs. Equivalent to `isFeatureAccessGranted(resolveFeatureAccessState(...))`
 * — kept as its own export so call sites that only gate on access do not have
 * to know the four-state vocabulary.
 */
export const canUseFeature = (
  assignment: FeatureActivationSource | null | undefined,
  defaultActivated: boolean | null | undefined,
): boolean =>
  isFeatureAccessGranted(
    resolveFeatureAccessState(assignment, defaultActivated),
  );

/**
 * Read `defaultActivated` off a Feature thing.
 *
 * `toClientRepres` flattens `attrs` onto the thing, so the flat field is the
 * shape every caller actually sees; the nested read is kept because the raw
 * DDB item shape reaches at least one caller unflattened, and a silent
 * `undefined` here reads as "default off" — the failure that hides access.
 */
export const readDefaultActivated = (feature: any): boolean =>
  feature?.defaultActivated === true ||
  feature?.attrs?.defaultActivated === true;

/** Read a Feature thing's refName, tolerating the assignment-side spelling. */
export const readFeatureRefName = (feature: any): string | undefined =>
  feature?.refName || feature?.featureRefName;

const toEpochMs = (v: unknown): number | undefined => {
  if (!v) return undefined;
  const ms = v instanceof Date ? v.getTime() : new Date(v as string).getTime();
  return Number.isNaN(ms) ? undefined : ms;
};

type Stamped = { activatedAt?: unknown; updatedAt?: unknown; createdAt?: unknown };

/**
 * WHEN a granted feature took effect for a tenant, epoch ms — or `undefined`
 * when access is not granted or no instant can be established.
 *
 * Most to least specific: the assignment's `activatedAt`, `updatedAt`,
 * `createdAt`; for `ActivatedByDefault` there is nothing tenant-specific, so
 * the Feature thing's own `updatedAt` (then `createdAt`) stands in. Shared so the
 * reminder scan and the WFE's manual-send dialog agree on "since activation"
 * (LOCKER-324 review F3.2).
 */
export const resolveFeatureActivatedAtMs = (
  state: FeatureAccessState,
  assignment: Stamped | null | undefined,
  feature: Stamped | null | undefined,
): number | undefined => {
  if (state === FeatureAccessState.ActivatedByAssignment) {
    return (
      toEpochMs(assignment?.activatedAt) ??
      toEpochMs(assignment?.updatedAt) ??
      toEpochMs(assignment?.createdAt)
    );
  }
  if (state === FeatureAccessState.ActivatedByDefault) {
    // `updatedAt` first: nothing records when `defaultActivated` was switched
    // on, and the Feature's `createdAt` can predate that by weeks — anchoring on
    // it would let a default flip reach back over the whole backlog, which is
    // what `beforeActivation` exists to prevent. `updatedAt` is never earlier
    // than the flip, so any error is toward fewer reminders (review F4.1).
    return toEpochMs(feature?.updatedAt) ?? toEpochMs(feature?.createdAt);
  }
  return undefined;
};
